import { useCallback, useEffect, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';

/**
 * Inactivity timeout threshold in milliseconds (30 minutes).
 */
const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000;

/**
 * Interval for checking session validity (1 minute).
 */
const CHECK_INTERVAL_MS = 60 * 1000;

/**
 * Pure function to determine if a session has expired due to inactivity.
 * Exported for property-based testing.
 *
 * @param lastActivityMs - Timestamp (ms since epoch) of the last user activity
 * @param nowMs - Current timestamp (ms since epoch)
 * @returns true if the session has expired (inactive for more than 30 minutes)
 */
export function isSessionExpired(lastActivityMs: number, nowMs: number): boolean {
  const elapsed = nowMs - lastActivityMs;
  return elapsed > INACTIVITY_TIMEOUT_MS;
}

/**
 * Custom React hook that manages session inactivity tracking.
 *
 * Behavior:
 * - Tracks last activity time (updates on mouse move, key press, clicks)
 * - Checks inactivity every minute
 * - If inactive for 30+ minutes, triggers MSAL logout redirect
 * - Provides `isSessionValid` state
 */
export function useSessionManager() {
  const { instance } = useMsal();
  const [isSessionValid, setIsSessionValid] = useState(true);
  const lastActivityRef = useRef<number>(Date.now());

  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  // Register activity listeners on mount
  useEffect(() => {
    const events: Array<keyof WindowEventMap> = ['mousemove', 'keypress', 'click'];

    events.forEach((event) => {
      window.addEventListener(event, updateActivity);
    });

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, updateActivity);
      });
    };
  }, [updateActivity]);

  // Periodically check for inactivity
  useEffect(() => {
    const intervalId = setInterval(() => {
      if (isSessionExpired(lastActivityRef.current, Date.now())) {
        setIsSessionValid(false);
        instance.logoutRedirect({
          postLogoutRedirectUri: window.location.origin,
        });
      }
    }, CHECK_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
    };
  }, [instance]);

  return { isSessionValid, updateActivity };
}
